import { useState, useRef, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import type { Action } from './usePerformAction';
import { StreamEventZ, type StreamSegment, type StreamChoice, type CompleteEvent } from '../stream-schema';
import type { components } from '../schema';
import { ApiError, type ProblemDetail } from '../client';
import { knownReaderSceneVersion, persistReaderEnvelope, readSceneEnvelope, sceneEnvelopeError, sceneVersion } from '../reader-envelope';

// API のベース URL はビルド時の環境変数から取得する。
// vite.config.ts で define または import.meta.env を通じて解決される。
const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

type Segment = components["schemas"]["Segment"];
type Choice = components["schemas"]["Choice"];
type ChoiceVisualCue = components["schemas"]["ChoiceVisualCue"];

export type StreamingMetrics = {
  model?: string;
  inputTokens?: number;
  outputTokens?: number;
  latencyMs?: number;
  promptSummary?: string;
};

export type SceneStreamState = {
  /** 正常終端で確定したSceneとcontextを一つのenvelopeとして保持する。 */
  scene?: components['schemas']['Scene'];
  segments: Segment[];
  choices: Choice[] | null;
  isEnding: boolean | null;
  isStreaming: boolean;
  error: string | null;
  /** HTTP拒否と検証拒否を、古いcache表示より優先するための最小状態。 */
  problem?: ProblemDetail;
  /** fixed playbackが停止すべきProblem Detailsを受け取った場合のみtrue。 */
  fixedContentUnavailable?: boolean;
  /** complete イベントで受信した計測値。streaming 完了前は null。未設定時は undefined。 */
  streamingMetrics?: StreamingMetrics | null;
};

export type SceneStreamActions = {
  /**
   * ストリーミングを開始する。
   * 既存のセグメントはリセットされ、新たにフェッチを開始する。
   */
  startStream: (playlineId: string, action: Action) => void;
  /** 状態を初期値にリセットする。進行中のフェッチはキャンセルする。 */
  reset: () => void;
};

function toApiSegment(segment: StreamSegment): Segment {
  if (segment.type === 'narration') {
    return { type: 'narration', text: segment.text };
  }
  return {
    type: 'dialogue',
    speakerId: segment.speakerId,
    speakerName: segment.speakerName,
    text: segment.text,
    ...(segment.cutin !== undefined ? { cutin: segment.cutin } : {}),
    ...(segment.cutinImageUrl !== undefined ? { cutinImageUrl: segment.cutinImageUrl } : {}),
    ...(segment.actingVariantId !== undefined
      ? { actingVariantId: segment.actingVariantId }
      : {}),
  };
}

function toApiChoiceVisualCue(
  visualCue: StreamChoice['visualCue'],
): ChoiceVisualCue | undefined {
  if (visualCue === undefined) return undefined;
  return {
    characterId: visualCue.characterId,
    actingVariantId: visualCue.actingVariantId,
    ...(visualCue.previewLayer !== undefined
      ? { previewLayer: visualCue.previewLayer }
      : {}),
    ...(visualCue.confirmLayer !== undefined
      ? { confirmLayer: visualCue.confirmLayer }
      : {}),
  };
}

function toApiChoice(choice: StreamChoice): Choice {
  const visualCue = toApiChoiceVisualCue(choice.visualCue);
  return {
    id: choice.id,
    text: choice.text,
    ...(choice.requiresMemory !== undefined
      ? { requiresMemory: choice.requiresMemory }
      : {}),
    ...(choice.emotion !== undefined ? { emotion: choice.emotion } : {}),
    ...(visualCue !== undefined ? { visualCue } : {}),
  };
}

const INITIAL_STATE: SceneStreamState = {
  segments: [],
  choices: null,
  isEnding: null,
  isStreaming: false,
  error: null,
  fixedContentUnavailable: false,
  streamingMetrics: null,
};

const HttpProblemZ = z.object({
  type: z.string(),
  title: z.string().optional(),
  detail: z.string().optional(),
  reason: z.string().optional(),
});

function invalidStream(reason: string): ApiError {
  return new ApiError(502, {
    type: '/errors/invalid-reader-envelope',
    title: 'Reader stream unavailable',
    status: 502,
    detail: 'Reader stream could not be verified.',
    reason,
  });
}

function fixedEventProblem(message: string): ProblemDetail | undefined {
  const match = /^(Fixed content unavailable|Fixed asset delivery unavailable): ([a-z_]{1,64})$/.exec(message);
  if (match === null) return undefined;
  const delivery = match[1] === 'Fixed asset delivery unavailable';
  return {
    type: delivery ? '/errors/fixed-asset-delivery-unavailable' : '/errors/fixed-content-unavailable',
    status: delivery ? 500 : 503,
    title: message,
    detail: '',
    ...(match[2] !== undefined ? { reason: match[2] } : {}),
  };
}

export function useSceneStream(options?: { generationMode?: string }): [SceneStreamState, SceneStreamActions] {
  const queryClient = useQueryClient();
  const generationMode = options?.generationMode;
  const [state, setState] = useState<SceneStreamState>(INITIAL_STATE);
  // アンマウント時に fetch をキャンセルするための AbortController を ref で管理する。
  // startStream を呼ぶたびに新しい controller を生成し、前の fetch を中断する。
  const controllerRef = useRef<AbortController | null>(null);

  // アンマウント時に進行中の fetch をキャンセルする。
  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
      controllerRef.current = null;
    };
  }, []);

  const startStream = useCallback((playlineId: string, action: Action) => {
    // 前の fetch があればキャンセルする。
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const isCurrent = (): boolean => controllerRef.current === controller && !controller.signal.aborted;

    // ストリーミング開始時にセグメントをリセットし、isStreaming を true にする。
    setState({
      segments: [],
      choices: null,
      isEnding: null,
      isStreaming: true,
      error: null,
      streamingMetrics: null,
    });

    const url = `${BASE_URL}/playlines/${playlineId}/actions/stream`;

    void (async () => {
      try {
        const queryKey = ['currentScene', playlineId];
        const initialVersion = await knownReaderSceneVersion(playlineId, queryClient.getQueryData(queryKey), generationMode);
        if (!isCurrent()) return;
        const fixed = generationMode === 'pre-baked' || initialVersion !== undefined;
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/x-ndjson',
          },
          body: JSON.stringify(action),
          signal: controller.signal,
        });
        if (!isCurrent()) return;

        if (!response.ok) {
          let fixedContentUnavailable = false;
          let problem: ProblemDetail = { type: 'about:blank', title: `HTTP ${response.status}`, status: response.status, detail: '' };
          try {
            const result = HttpProblemZ.safeParse(await response.json());
            if (result.success) {
              const body = result.data;
              problem = {
                type: body.type,
                title: body.title ?? `HTTP ${response.status}`,
                status: response.status,
                detail: body.detail ?? '',
                ...(body.reason !== undefined && /^[a-z_]{1,64}$/.test(body.reason) ? { reason: body.reason } : {}),
              };
              fixedContentUnavailable = response.status === 503 && body.type === '/errors/fixed-content-unavailable';
            }
          } catch {
            // 非JSONの失敗は既存の一般ストリーミングエラーとして扱う。
          }
          if (!isCurrent()) return;
          setState((prev) => ({
            ...prev,
            isStreaming: false,
            error: `HTTP error ${response.status}`,
            fixedContentUnavailable,
            problem,
          }));
          return;
        }

        if (response.body === null) {
          setState((prev) => ({
            ...prev,
            isStreaming: false,
            error: 'Response body is null',
          }));
          return;
        }

        // TextDecoderStream で ReadableStream<Uint8Array> を ReadableStream<string> に変換し、
        // 行ごとに NDJSON を読み取る。
        const reader = response.body
          .pipeThrough(new TextDecoderStream())
          .getReader();

        // 不完全な行を跨いで受信するケースに備えてバッファを使う。
        let buffer = '';
        let pendingComplete: CompleteEvent['data'] | undefined;
        let invalidFrameSeen = false;

        const processLine = (line: string): void => {
          const trimmed = line.trim();
          if (trimmed === '') return;
          let parsed: unknown;
          try {
            parsed = JSON.parse(trimmed);
          } catch {
            invalidFrameSeen = true;
            if (fixed || pendingComplete !== undefined) throw invalidStream('invalid_stream_event');
            console.warn('[useSceneStream] Invalid NDJSON frame');
            return;
          }
          const result = StreamEventZ.safeParse(parsed);
          if (!result.success) {
            invalidFrameSeen = true;
            // Scene付きcompleteの不正をruntimeの古い寛容parserで読み飛ばさない。
            const hasScene = typeof parsed === 'object' && parsed !== null && 'data' in parsed
              && typeof parsed.data === 'object' && parsed.data !== null && 'scene' in parsed.data;
            if (fixed || hasScene || pendingComplete !== undefined) throw invalidStream('invalid_stream_event');
            console.warn('[useSceneStream] Invalid stream event');
            return;
          }
          const event = result.data;
          if (event.type === 'error') {
            const problem = fixedEventProblem(event.data.message);
            if (problem !== undefined) throw new ApiError(problem.status, problem);
            throw new Error(event.data.message);
          }
          if (pendingComplete !== undefined) throw invalidStream('invalid_stream_event');
          if (event.type === 'segment') {
            setState((prev) => isCurrent() ? { ...prev, segments: [...prev.segments, toApiSegment(event.data)] } : prev);
          } else {
            pendingComplete = event.data;
          }
        };

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (!isCurrent()) {
              await reader.cancel();
              return;
            }
            if (done) break;
            buffer += value;
            const lines = buffer.split('\n');
            buffer = lines.pop() ?? '';
            for (const line of lines) processLine(line);
          }
          processLine(buffer);
        } finally {
          // error frameで終わった応答もreaderを解放し、成功として扱わない。
          void reader.cancel().catch(() => undefined);
          reader.releaseLock();
        }

        const currentVersion = await knownReaderSceneVersion(playlineId, queryClient.getQueryData(queryKey), generationMode);
        if (!isCurrent()) return;

        // IDBを待つ間にもmemory pinは変わり得る。ここから保存まで非同期処理を挟まない。
        const latestMemory = queryClient.getQueryData(queryKey);
        const latestMemoryVersion = sceneEnvelopeError(latestMemory, generationMode) === null ? sceneVersion(latestMemory) : undefined;
        const knownVersions = [initialVersion, currentVersion, latestMemoryVersion]
          .filter((version): version is string => version !== undefined);
        if (new Set(knownVersions).size > 1) {
          throw invalidStream('fixed_version_mismatch');
        }
        const finalVersion = latestMemoryVersion ?? currentVersion ?? initialVersion;
        const fixedAtEnd = fixed || finalVersion !== undefined;
        const complete = pendingComplete;
        if (complete === undefined) {
          if (fixedAtEnd) throw invalidStream('stream_incomplete');
          setState((prev) => isCurrent() ? { ...prev, isStreaming: false } : prev);
          return;
        }
        if (fixedAtEnd && complete.scene === undefined) throw invalidStream('fixed_context_required');
        // 正常EOFとpin確認を待ってから、途中segmentsではなく受信Scene全体を確定する。
        const scene = complete.scene !== undefined ? readSceneEnvelope(complete.scene, generationMode, finalVersion) : undefined;
        if (invalidFrameSeen && scene?.fixedContentVersionId != null) throw invalidStream('invalid_stream_event');
        const streamingMetrics: StreamingMetrics = {
          ...(complete.model !== undefined ? { model: complete.model } : {}),
          ...(complete.inputTokens !== undefined ? { inputTokens: complete.inputTokens } : {}),
          ...(complete.outputTokens !== undefined ? { outputTokens: complete.outputTokens } : {}),
          ...(complete.latencyMs !== undefined ? { latencyMs: complete.latencyMs } : {}),
          ...(complete.promptSummary !== undefined ? { promptSummary: complete.promptSummary } : {}),
        };
        if (scene !== undefined) {
          queryClient.setQueryData(queryKey, scene);
          persistReaderEnvelope('scene', playlineId, scene);
          setState({
            scene,
            segments: scene.segments,
            choices: scene.choices,
            isEnding: scene.isEnding,
            isStreaming: false,
            error: null,
            fixedContentUnavailable: false,
            streamingMetrics,
          });
        } else {
          setState((prev) => isCurrent() ? { ...prev, choices: complete.choices.map(toApiChoice), isEnding: complete.isEnding, isStreaming: false, streamingMetrics } : prev);
        }
      } catch (err) {
        if (!isCurrent()) return;
        // AbortError はアンマウントや reset による意図的キャンセルなので無視する。
        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        }
        // ネットワークエラー等: セグメントはリセットせず error を設定する。
        const message = err instanceof Error ? err.message : 'Network error';
        setState((prev) => ({
          ...prev,
          isStreaming: false,
          error: message,
          ...(err instanceof ApiError ? {
            problem: err.problem,
            fixedContentUnavailable: err.problem.type === '/errors/fixed-content-unavailable',
          } : {}),
        }));
      }
    })();
  }, [queryClient, generationMode]);

  const reset = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    setState(INITIAL_STATE);
  }, []);

  return [state, { startStream, reset }];
}
